import type { Cards } from 'api/types/cards.types';
import Button from 'components/atoms/Button';
import { BUTTON_NAMES } from 'constant/button-names';
import React, { useRef } from 'react';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { formatCreditCardNumber, formatCVC, formatExpirationDate } from 'utils/validation/payments';

import BottomSheet from '../BottomSheet';

interface Props {
  show: boolean;
  isAddCardButtonDisabled: boolean;
  onSubmit(card: Cards.CardForm): any;
  onClose?(): any;
}
type CardForm = {
  cardNumber: string;
  expirationDate: string;
  cvv: string;
};

const StyledFormLabel = styled(Form)`
  ${({ theme }) =>
    theme.components.paymentMethodSheet &&
    `
  color: ${theme.components.paymentMethodSheet.label?.textColor}`};
  font-size: 13px;
`;

const AddPaymentCardSheet: React.FC<Props> = ({ ...props }) => {
  const expiryDateRef = useRef<HTMLInputElement | null>(null);
  const cvvCardRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const { register, errors, handleSubmit, trigger } = useForm<Cards.CardForm>({
    mode: 'onChange',
    defaultValues: { cardNumber: '', expirationDate: '', cvv: '' },
  });

  const submitHandler = async (data: Cards.CardForm) => {
    const card: Cards.CardForm = {
      ...data,
      cardNumber: data.cardNumber.replace(/\s/g, ''), // remove spaces from string
    };
    props.onSubmit(card);
  };

  const errorHandler = () => {};

  const handleInputChange = async (event: any, nextRef?: any) => {
    const { target } = event;
    const { name, value } = target;

    let modifiedValue = '';

    const updateValueAndFocus = async (formattedValue: string, regexMatchCondition: boolean, focusCondition: boolean) => {
      target.value = formattedValue;
      const isMatchRegex = await trigger(name as keyof CardForm);
      if (isMatchRegex && regexMatchCondition && focusCondition && nextRef) {
        nextRef.current?.focus();
      }
    };

    if (name === 'cardNumber') {
      const { value: formattedValue, expectedLength } = formatCreditCardNumber(value);
      if (formattedValue) {
        await updateValueAndFocus(formattedValue, formattedValue.length === expectedLength, true);
      }
    } else if (name === 'expirationDate') {
      modifiedValue = formatExpirationDate(value);
      target.value = modifiedValue;
      if (modifiedValue.length === 5) {
        cvvCardRef.current?.focus();
      }
    } else if (name === 'cvv') {
      modifiedValue = formatCVC(value);
      target.value = modifiedValue;
    }
  };

  const setRefAndRegister = (ref: React.MutableRefObject<HTMLInputElement | null>, validationRules: Object) => (e: HTMLInputElement) => {
    if (e) {
      const currentRef = ref;
      currentRef.current = e;
      register(e, validationRules);
    }
  };
  return (
    <BottomSheet show={props.show} onClose={props.onClose} title={t('common.addPaymentCard')}>
      <Row>
        <Col xs={12}>
          <Form onSubmit={handleSubmit(submitHandler, errorHandler)}>
            <Form.Row>
              <Col xs={12}>
                <Form.Group>
                  <StyledFormLabel>{t('common.cardNumber')}</StyledFormLabel>
                  <Form.Control
                    className="replay-masked-input"
                    type="tel"
                    name="cardNumber"
                    autoFocus
                    placeholder={t('common.cardNumber')}
                    autoComplete="off"
                    inputMode="numeric"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, expiryDateRef)}
                    ref={register({
                      required: t('common.pleaseEnterCardNumber') as string,
                      pattern: {
                        value: /[\d| ]{16,22}/,
                        message: t('common.cardNumberValidation'),
                      },
                    })}
                    isInvalid={Boolean(errors.cardNumber)}
                  />
                  <Form.Control.Feedback type="invalid">{errors.cardNumber?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Form.Row>
            <Form.Row>
              <Col>
                <Form.Group>
                  <StyledFormLabel>{t('common.expiration')}</StyledFormLabel>
                  <Form.Control
                    className="replay-masked-input"
                    type="tel"
                    name="expirationDate"
                    placeholder="MM/YY"
                    autoComplete="off"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, cvvCardRef)}
                    ref={setRefAndRegister(expiryDateRef, {
                      required: 'Please enter the expiration date.',
                      pattern: {
                        value: /\d\d\/\d\d/,
                        message: 'Invalid expiration date format.',
                      },
                    })}
                    isInvalid={Boolean(errors.expirationDate)}
                  />
                  <Form.Control.Feedback type="invalid">{errors.expirationDate?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <StyledFormLabel>{t('common.cvv')}</StyledFormLabel>
                  <Form.Control
                    className="replay-masked-input"
                    type="tel"
                    name="cvv"
                    placeholder="CVV"
                    autoComplete="off"
                    onChange={handleInputChange}
                    ref={setRefAndRegister(cvvCardRef, {
                      required: t('common.pleaseEnterCVV') as string,
                      pattern: {
                        value: /\d{3,4}/,
                        message: t('common.enterCVVValidation'),
                      },
                    })}
                    isInvalid={Boolean(errors.cvv)}
                  />
                  <Form.Control.Feedback type="invalid">{errors.cvv?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Form.Row>
            <Form.Row>
              <Col xs={12}>
                <Button
                  textColor={theme.components.pages.home?.placeOrderFooter?.textColor}
                  backgroundColor={theme.components.pages.home?.placeOrderFooter?.backgroundColor}
                  borderColor={theme.components.pages.home?.placeOrderFooter?.borderColor}
                  block
                  size="lg"
                  type="submit"
                  disabled={props.isAddCardButtonDisabled}
                  buttonName={BUTTON_NAMES.ADD_PAYMENT_CARD}
                >
                  {t('common.addCard')}
                </Button>
              </Col>
            </Form.Row>
          </Form>
        </Col>
      </Row>
    </BottomSheet>
  );
};

export default AddPaymentCardSheet;
